import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { mq, overflowBreakWord } from '@hrworks/design-system/utilities';

import type { TooltipProps } from './Tooltip.types';

const tooltipAnimation = keyframes({
  '0%': {
    opacity: 0,
    scale: 0.7,
    visibility: 'hidden',
  },
  '100%': {
    opacity: 1,
    scale: 1,
    visibility: 'visible',
  },
});

type TriggerProps = Pick<TooltipProps, 'fullWidth' | 'unstyledTrigger'>;

const Trigger = styled.div<TriggerProps>(({ fullWidth, unstyledTrigger }) => ({
  ...(!unstyledTrigger && {
    display: 'block',
    ...(!fullWidth && {
      display: 'flex',
      alignItems: 'center',
      width: 'fit-content',
    }),
  }),
  [mq.isTouchDevice]: {
    '*': {
      userSelect: 'none',
    },
  },
}));

type TooltipContainerProps = {
  playAnimation?: boolean;
  isCustom?: boolean;
} & Pick<Required<TooltipProps>, 'placement'>;

const TooltipContainer = styled.div<TooltipContainerProps>(({ theme, playAnimation, placement, isCustom }) => {
  const transformOriginDirection = {
    top: 'bottom',
    left: 'right',
    right: 'left',
    bottom: 'top',
  };

  return [
    overflowBreakWord,
    !isCustom && { boxShadow: theme.eclipseTier2Color.elevation.shadow.level1.boxShadow },
    {
      maxWidth: 300,
      display: 'flex',
      flexDirection: 'column',
      width: 'max-content',
      willChange: 'transform',
      transformOrigin: transformOriginDirection[placement.split('-')[0] as keyof typeof transformOriginDirection],
      pointerEvents: 'none',
      backgroundColor: 'transparent',
      ...(playAnimation && {
        animation: `${tooltipAnimation} cubic-bezier(0.4, 0, 0.2, 1) ${theme.marko.variables.animationDuration.normal} forwards`,
      }),
      ...(!isCustom && {
        padding: theme.eclipseTier1.spacing['12px'],
        borderRadius: theme.eclipseTier1.radius['12px'],
        backgroundColor: theme.eclipseTier2Color.background.neutral.bold,
        color: theme.eclipseTier2Color.text.inverse,
        gap: theme.eclipseTier1.spacing['8px'],
      }),
    },
  ];
});

export const S = {
  Trigger,
  TooltipContainer,
} as const;
